import { useInView } from "@/hooks/useInView";
import { useEffect, useState } from "react";
import { useSpring, useTransform } from "framer-motion";

// Custom component to handle B/M/K formatting with live updating
function AnimatedMoney({ value, initialValue }: { value: number; initialValue: number }) {
  const spring = useSpring(initialValue, {
    stiffness: 15, // Slower (was 20)
    damping: 50,   // More damping for smoother motion
    restDelta: 0.001
  });

  const display = useTransform(spring, (current) => {
    const absValue = Math.abs(current);

    if (absValue >= 1000000000) {
      return `$${(absValue / 1000000000).toFixed(1)}B`;
    } else if (absValue >= 1000000) {
      return `$${Math.round(absValue / 1000000)}M`;
    } else if (absValue >= 1000) {
      return `$${Math.round(absValue / 1000)}K`;
    } else {
      return `$${Math.round(absValue)}`;
    }
  });

  const [displayText, setDisplayText] = useState(() => {
    const absValue = Math.abs(initialValue);
    if (absValue >= 1000000000) {
      return `$${(absValue / 1000000000).toFixed(1)}B`;
    } else if (absValue >= 1000000) {
      return `$${Math.round(absValue / 1000000)}M`;
    } else if (absValue >= 1000) {
      return `$${Math.round(absValue / 1000)}K`;
    } else {
      return `$${Math.round(absValue)}`;
    }
  });

  useEffect(() => {
    spring.set(value);
  }, [spring, value]);

  useEffect(() => {
    const unsubscribe = display.on("change", (v) => setDisplayText(v));
    return unsubscribe;
  }, [display]);

  return <>{displayText}</>;
}

export function ResponseTime() {
  const { ref, isInView } = useInView({
    threshold: 0.3,
    triggerOnce: false // Allow re-triggering for loop
  });

  const [lossesValue, setLossesValue] = useState(1000000000); // Start at $1B
  const [revenueValue, setRevenueValue] = useState(0); // Start at $0
  const [growthValue, setGrowthValue] = useState(0); // Start at 0%

  const growthSpring = useSpring(0, { // Initialize from 0
    stiffness: 20,  // Slower (was 25)
    damping: 50,    // More damping
    restDelta: 0.001
  });

  const growthDisplay = useTransform(growthSpring, (current) =>
    `${Math.round(current)}%`
  );

  const [growthText, setGrowthText] = useState("0%");

  useEffect(() => {
    growthSpring.set(growthValue);
  }, [growthSpring, growthValue]);

  useEffect(() => {
    const unsubscribe = growthDisplay.on("change", (v) => setGrowthText(v));
    return unsubscribe;
  }, [growthDisplay]);

  // Loop animation when in view
  useEffect(() => {
    if (!isInView) return;

    // Animate from start to end over 12 seconds (slower)
    const animateForward = () => {
      setLossesValue(0); // $1B → $0
      setRevenueValue(1000000000); // $0 → $1B
      setGrowthValue(300); // 0% → 300%
    };

    // Reset and loop
    const animateLoop = () => {
      // Reset to start values
      setLossesValue(1000000000);
      setRevenueValue(0);
      setGrowthValue(0);

      // Start animation after brief pause
      setTimeout(animateForward, 1000); // Longer pause before starting
    };

    // Start initial animation
    animateForward();

    // Loop every 14 seconds (12s animation + 2s pause)
    const interval = setInterval(animateLoop, 14000);

    return () => clearInterval(interval);
  }, [isInView]);

  return (
    <div ref={ref} className="w-full h-full flex flex-col gap-4">
      <div className="grid grid-cols-2 [@media(min-width:390px)]:grid-cols-3 gap-3 flex-1 min-h-[200px]">
        {/* Decrease Losses */}
        <div className="bg-card border border-border rounded-3xl p-4 flex flex-col items-center justify-center text-center transition-all duration-300 hover:border-primary/40 hover:transform hover:-translate-y-1 hover:shadow-lg">
          <span className="font-heading text-foreground text-2xl mb-4">
            -<AnimatedMoney value={lossesValue} initialValue={1000000000} />
          </span>
          <span className="text-red-500 font-body font-medium mb-1">Decrease</span>
          <span className="text-muted-foreground font-light text-sm">Losses</span>
        </div>

        {/* Growth Rate */}
        <div className="bg-card border border-border rounded-3xl p-4 flex flex-col items-center justify-center text-center transition-all duration-300 hover:border-primary/40 hover:transform hover:-translate-y-1 hover:shadow-lg">
          <span className="font-heading text-foreground text-2xl mb-4">
            {growthText}
          </span>
          <span className="text-yellow-500 font-body font-medium mb-1">Increase</span>
          <span className="text-muted-foreground font-light text-sm">Growth</span>
        </div>

        {/* Increase Revenue */}
        <div className="bg-card border border-border rounded-3xl p-4 col-span-2 [@media(min-width:390px)]:col-span-1 flex flex-col items-center justify-center text-center transition-all duration-300 hover:border-primary/40 hover:transform hover:-translate-y-1 hover:shadow-lg">
          <span className="font-heading text-foreground text-2xl mb-4">
            +<AnimatedMoney value={revenueValue} initialValue={0} />
          </span>
          <span className="text-green-500 font-body font-medium mb-1">Increase</span>
          <span className="text-muted-foreground font-light text-sm">Revenue</span>
        </div>
      </div>
    </div>
  );
}